import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import {
  AccessRequiredComponent,
  LoginComponent,
  NotFoundComponent,
} from '@tw/components';
import { UnauthorizedComponent } from 'src/shared/components/unauthorized/unauthorized.component';
import { permissionGuard } from 'src/shared/guards/permission-guard';
import { viewDetailMemberResolver } from './pages/my-team/view-detail/view-detail.resolver';
import {
  PermissionAction,
  PermissionSubject,
} from 'src/shared/models/permissions';
import { AppLayoutComponent } from './app-layout.component';
import { CalendarComponent } from './pages/calendar/calendar.component';
import { ClientsComponent } from './pages/clients/clients.component';
import { MyInfoComponent } from './pages/my-info/my-info.component';
import { SupersetsComponent } from './pages/supersets/supersets.component';
import { TrackComponent } from './pages/track/track.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'app',
    pathMatch: 'full',
  },
  {
    matcher: (segments) => {
      const regexPattern = /^code.*$/i;
      const [firstSegment] = segments;
      const isMatch = regexPattern.test(firstSegment.path);
      return isMatch ? { consumed: segments } : null;
    },
    redirectTo: 'app',
  },
  {
    path: 'app',
    canActivate: [MsalGuard],
    canActivateChild: [permissionGuard],
    component: AppLayoutComponent,
    children: [
      { path: '', redirectTo: 'track', pathMatch: 'full' },
      {
        path: 'track',
        title: 'Track',
        component: TrackComponent,
        data: {
          permissions: [
            {
              subject: PermissionSubject.TimeEntry,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'calendar',
        title: 'Calendar',
        component: CalendarComponent,
      },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then(
            (c) => c.DashboardComponent,
          ),
        data: {
          permissions: [
            {
              subject: PermissionSubject.Dashboard,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'reports',
        title: 'Reports',
        loadComponent: () =>
          import('./pages/reports/reports.component').then(
            (c) => c.ReportsComponent,
          ),
        loadChildren: () =>
          import('./pages/reports/reports.routes').then((m) => m.REPORTS_ROUTES),
        data: {
          permissions: [
            {
              subject: PermissionSubject.Report,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'projects',
        title: 'Projects',
        loadComponent: () =>
          import('./pages/projects/projects.component').then(
            (c) => c.ProjectsComponent,
          ),
        data: {
          permissions: [
            {
              subject: PermissionSubject.Project,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'team',
        title: 'Team',
        loadComponent: () =>
          import('./pages/teams/teams.component').then((c) => c.TeamsComponent),
        loadChildren: () =>
          import('./pages/teams/teams.routes').then((m) => m.TEAMS_ROUTES),
        data: {
          permissions: [
            {
              subject: PermissionSubject.ProjectUserAssignment,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'view-user/:id',
        title: 'View User',
        loadComponent: () =>
          import('./pages/teams/view-user/view-user.component').then(
            (c) => c.ViewUserComponent,
          ),
        data: {
          permissions: [
            {
              subject: PermissionSubject.ProjectUserAssignment,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'my-team',
        title: 'My Team',
        loadComponent: () =>
          import('./pages/my-team/my-team.component').then(
            (c) => c.MyTeamComponent,
          ),
        loadChildren: () =>
          import('./pages/my-team/my-team.routes').then(
            (m) => m.MY_TEAM_ROUTES,
          ),
        data: {
          permissions: [
            {
              subject: PermissionSubject.MyTeam,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'my-team/:id',
        loadComponent: () =>
          import('./pages/my-team/view-detail/view-detail.component').then(
            (c) => c.ViewDetailComponent,
          ),
        data: {
          permissions: [
            {
              subject: PermissionSubject.MyTeam,
              action: PermissionAction.View,
            },
          ],
        },
        resolve: { member: viewDetailMemberResolver },
        children: [
          { path: '', redirectTo: 'user-info', pathMatch: 'full' },
          {
            path: 'user-info',
            loadComponent: () =>
              import(
                './pages/my-team/view-detail/user-info/user-info.component'
              ).then((c) => c.UserInfoComponent),
          },
          {
            path: 'time-entries',
            loadComponent: () =>
              import(
                './pages/my-team/view-detail/time-entries/time-entries.component'
              ).then((c) => c.TimeEntriesComponent),
          },
        ],
      },
      {
        path: 'clients',
        title: 'Clients',
        component: ClientsComponent,
        data: {
          permissions: [
            {
              subject: PermissionSubject.Client,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'tasktypes',
        title: 'Task Types',
        loadComponent: () =>
          import('./pages/tasktypes/tasktypes.component').then(
            (c) => c.TasktypesComponent,
          ),
        data: {
          permissions: [
            {
              subject: PermissionSubject.TaskType,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'roletypes',
        title: 'Access Role Types',
        loadComponent: () =>
          import('./pages/roletypes/roletypes.component').then(
            (c) => c.RoletypesComponent,
          ),
        data: {
          permissions: [
            {
              subject: PermissionSubject.RoleTaskAssignment,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'settings',
        title: 'Settings',
        component: NotFoundComponent,
        data: {
          permissions: [
            {
              subject: PermissionSubject.RolePermission,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'superset',
        title: 'Superset',
        component: SupersetsComponent,
        data: {
          permissions: [
            {
              subject: PermissionSubject.SuperSet,
              action: PermissionAction.View,
            },
          ],
        },
      },
      {
        path: 'my-info',
        title: 'My Info',
        component: MyInfoComponent,
      },
      { path: '**', component: NotFoundComponent },
    ],
  },
  { path: 'login', component: LoginComponent },
  { path: 'unauthorized', component: UnauthorizedComponent },
  { path: 'access-required', component: AccessRequiredComponent },
  { path: '**', component: NotFoundComponent },
];
